/**
 * Classify customer ledger rows for view-popup clicks.
 * Prefer entry_type / doc_type / ids from rawEntry; fall back to type/remarks text.
 *
 * Payment settlements on a parent bill (e.g. "CASH - COR_0021", "BANK - SAL_0817")
 * are NOT clickable — open the parent COR/SAL/REP/URD instead.
 * Standalone cashbook vouchers (e.g. "UPI - BREC_0002") ARE clickable.
 */

function typeText(row) {
  return String(row?.type || "").trim();
}

function remarksText(row) {
  return String(row?.remarks || "").trim();
}

function entryType(row) {
  return String(row?.rawEntry?.entry_type || "").trim().toLowerCase();
}

function docType(row) {
  return String(row?.rawEntry?.doc_type || "").trim().toUpperCase();
}

function combinedText(row) {
  return `${typeText(row)} ${remarksText(row)}`.toLowerCase();
}

/** Cashbook voucher doc prefixes (bank/cash receipt & payment). */
const CASHBOOK_VOUCHER_DOC_RE =
  /\b((?:BREC|CREC|BPAY|CPAY|BIN|BOUT|CIN|COUT|JV|CV)[_-][A-Za-z0-9_-]+)/i;

/** Parent bill/order doc on a payment settlement line. */
const PARENT_BILL_DOC_RE =
  /\b((?:QSAL|SAL|QCOR|COR|ORD|QREP|REP|URD|PUR)[_-][A-Za-z0-9_-]+)/i;

function rowLooksLikeCashbookVoucher(row) {
  if (!row) return false;
  const t = typeText(row);
  if (CASHBOOK_VOUCHER_DOC_RE.test(t)) return true;
  if (CASHBOOK_VOUCHER_DOC_RE.test(remarksText(row))) return true;
  const raw = row.rawEntry || {};
  if (raw.doc_number && CASHBOOK_VOUCHER_DOC_RE.test(String(raw.doc_number))) {
    return true;
  }
  if (
    raw.voucher_number &&
    CASHBOOK_VOUCHER_DOC_RE.test(String(raw.voucher_number))
  ) {
    return true;
  }
  return false;
}

/**
 * Payment applied against an existing bill/order — already visible on that bill.
 * e.g. "CASH - COR_0021", "UPI - SAL_0817", "BANK - REP_12"
 * NOT "UPI - BREC_0002" (standalone cashbook voucher).
 */
export function isBillPaymentSettlementLine(row) {
  if (!row || row.isOpening) return false;
  // Standalone vouchers always win over payment heuristics
  if (rowLooksLikeCashbookVoucher(row)) return false;

  const t = typeText(row);
  // Mode + parent doc: "CASH - COR_0021", "BANK - SAL_0817", "UPI - SAL_0817"
  if (
    /^(CASH|BANK|UPI|CARD|CHEQUE|OLD[_\s-]?GOLD|OLD[_\s-]?SILVER|SCHEME|KASAR)\s*[-:]/i.test(
      t,
    ) &&
    PARENT_BILL_DOC_RE.test(t)
  ) {
    return true;
  }

  const et = entryType(row);
  if (
    et === "sale_payment" ||
    et === "urd_sale_payment" ||
    et === "order_payment" ||
    et === "custom_order_payment" ||
    et === "repair_payment" ||
    et === "repairing_payment"
  ) {
    return true;
  }
  return false;
}

export function isCustomOrderLedgerRow(row) {
  if (!row || row.isOpening) return false;
  // "CASH - COR_0021" is a payment on the order, not the order itself
  if (isBillPaymentSettlementLine(row)) return false;

  const t = typeText(row);
  if (
    /^(custom\s*order|order|q?cor)\b/i.test(t) ||
    /^Q?COR[_-]/i.test(t) ||
    /^ORD[_-]/i.test(t)
  ) {
    return true;
  }

  const et = entryType(row);
  if (
    et === "order_due" ||
    et === "custom_order_due" ||
    et === "custom_order" ||
    et === "order"
  ) {
    return true;
  }
  const dt = docType(row);
  if (dt.includes("CUSTOM_ORDER") || dt === "ORDER") {
    // Only if type isn't a payment-mode prefix
    if (!/^(CASH|BANK|UPI|CARD|CHEQUE)\b/i.test(t)) return true;
  }
  if (row.rawEntry?.custom_order_id && !/^(CASH|BANK|UPI|CARD|CHEQUE)\b/i.test(t)) {
    return true;
  }
  return false;
}

export function isRepairLedgerRow(row) {
  if (!row || row.isOpening) return false;
  if (isBillPaymentSettlementLine(row)) return false;

  const t = typeText(row);
  if (/^(repair|q?rep)\b/i.test(t) || /^Q?REP[_-]/i.test(t)) return true;

  if (row.rawEntry?.repairing_order_id || row.rawEntry?.repair_order_id) {
    if (!/^(CASH|BANK|UPI|CARD|CHEQUE)\b/i.test(t)) return true;
  }
  const et = entryType(row);
  if (et === "repair_due" || et === "repairing_due" || et === "repair") {
    return true;
  }
  const dt = docType(row);
  if (dt.includes("REPAIR") && !/^(CASH|BANK|UPI|CARD|CHEQUE)\b/i.test(t)) {
    return true;
  }
  return false;
}

/** Standalone cash / bank voucher rows (BREC / CREC / …), not bill payments. */
export function isCashbookVoucherLedgerRow(row) {
  if (!row || row.isOpening) return false;

  // "UPI - BREC_0002" — detect voucher doc first (before payment filters)
  if (rowLooksLikeCashbookVoucher(row)) return true;

  // Bill settlements like "CASH - COR_0021" / "UPI - SAL_0817"
  if (isBillPaymentSettlementLine(row)) return false;

  const et = entryType(row);
  if (
    et === "sale_payment" ||
    et === "sale_due" ||
    et === "urd_sale_payment" ||
    et === "urd" ||
    et === "order_due" ||
    et === "custom_order_due"
  ) {
    return false;
  }

  const t = typeText(row);
  if (row.rawEntry?.cash_transaction_id) return true;
  if (row.rawEntry?.tx_type) {
    const tx = String(row.rawEntry.tx_type).toUpperCase();
    if (/^(CASH|BANK)_(IN|OUT)$/.test(tx)) return true;
  }
  if (
    ["cash_in", "cash_out", "bank_in", "bank_out", "cashbook", "voucher"].includes(
      et,
    )
  ) {
    return true;
  }
  const dt = docType(row);
  if (
    dt === "CASHBOOK" ||
    dt === "VOUCHER" ||
    dt === "CASH_IN" ||
    dt === "CASH_OUT" ||
    dt === "BANK_IN" ||
    dt === "BANK_OUT"
  ) {
    return true;
  }
  if (/\b(cash|bank)\s*[-:]?\s*(in|out)\b/i.test(t)) return true;
  if (/^(CASH|BANK)_(IN|OUT)\b/i.test(t)) return true;
  return false;
}

export function extractCustomOrderId(row) {
  return (
    row?.rawEntry?.custom_order_id ||
    row?.rawEntry?.document_id ||
    row?.rawEntry?.order_id ||
    row?.rawEntry?.id ||
    null
  );
}

export function extractCustomOrderDocNumber(row) {
  if (!row) return "";
  const fromRaw =
    row.rawEntry?.doc_number ||
    row.rawEntry?.order_number ||
    row.rawEntry?.co_number ||
    row.rawEntry?.voucher_number ||
    "";
  if (fromRaw) return String(fromRaw).trim();
  const typeStr = typeText(row);
  const m =
    typeStr.match(/(?:custom\s*order|order)\s*[-:]\s*([A-Za-z0-9_-]+)/i) ||
    typeStr.match(/((?:QCOR|COR|ORD|CO)[_-][A-Za-z0-9_-]+)/i);
  return m?.[1]?.trim() || "";
}

export function extractRepairingOrderId(row) {
  return (
    row?.rawEntry?.repairing_order_id ||
    row?.rawEntry?.repair_order_id ||
    row?.rawEntry?.document_id ||
    row?.rawEntry?.id ||
    null
  );
}

export function extractRepairDocNumber(row) {
  if (!row) return "";
  const fromRaw =
    row.rawEntry?.doc_number ||
    row.rawEntry?.order_number ||
    row.rawEntry?.token_no ||
    row.rawEntry?.voucher_number ||
    "";
  if (fromRaw) return String(fromRaw).trim();
  const typeStr = typeText(row);
  const m =
    typeStr.match(/(?:repair(?:ing)?(?:\s*order)?)\s*[-:]\s*([A-Za-z0-9_-]+)/i) ||
    typeStr.match(/((?:REP)[_-][A-Za-z0-9_-]+)/i);
  return m?.[1]?.trim() || "";
}

/** CREC / BREC / CPAY / BPAY — show id is cash_transaction_id (not sale document_id). */
const CASH_TX_VOUCHER_PREFIX_RE = /\b(BREC|CREC|BPAY|CPAY)[_-]/i;

export function isCashTxVoucherLedgerRow(row) {
  if (!row) return false;
  const raw = row.rawEntry || {};
  const candidates = [
    typeText(row),
    remarksText(row),
    raw.voucher_number,
    raw.doc_number,
  ];
  return candidates.some((v) => CASH_TX_VOUCHER_PREFIX_RE.test(String(v || "")));
}

export function extractCashbookVoucherShowId(row) {
  if (!row) return null;
  const raw = row.rawEntry || {};

  // CREC / BREC / CPAY / BPAY: always use cash_transaction_id (document_id is the parent bill).
  if (isCashTxVoucherLedgerRow(row)) {
    if (raw.cash_transaction_id != null && raw.cash_transaction_id !== "") {
      return raw.cash_transaction_id;
    }
    return null;
  }

  if (raw.cash_transaction_id != null && raw.cash_transaction_id !== "") {
    return raw.cash_transaction_id;
  }
  if (raw.document_id != null && raw.document_id !== "") {
    return raw.document_id;
  }
  if (raw.doc_number != null && String(raw.doc_number).trim() !== "") {
    return String(raw.doc_number).trim();
  }
  if (raw.voucher_number != null && String(raw.voucher_number).trim() !== "") {
    return String(raw.voucher_number).trim();
  }
  const fromType = typeText(row).match(CASHBOOK_VOUCHER_DOC_RE);
  if (fromType?.[1]) return fromType[1].trim();
  const fromRemarks = remarksText(row).match(CASHBOOK_VOUCHER_DOC_RE);
  if (fromRemarks?.[1]) return fromRemarks[1].trim();
  return null;
}

/**
 * Mixed book: cash/bank voucher host from bill_type.
 * Quotation → /quote, GST → /api.
 * @returns {boolean|undefined}
 */
export function resolveCashbookVoucherIsQuote(source) {
  if (source == null) return undefined;
  if (typeof source === "boolean") return source;
  const asObj = typeof source === "object" ? source : null;
  if (asObj?.isQuote !== undefined && asObj?.isQuote !== null) {
    return Boolean(asObj.isQuote);
  }
  const billType = String(
    asObj?.bill_type ??
      asObj?.billType ??
      asObj?.bill_type_name ??
      (typeof source === "string" || typeof source === "number" ? source : ""),
  )
    .trim()
    .toLowerCase();
  if (!billType) return undefined;
  if (
    billType === "quotation" ||
    billType === "quote" ||
    billType === "estimate" ||
    billType.includes("quotation")
  ) {
    return true;
  }
  if (
    billType === "gst" ||
    billType === "tax" ||
    billType.includes("gst") ||
    billType === "invoice"
  ) {
    return false;
  }
  return undefined;
}

function pickDocUpper(...values) {
  for (const v of values) {
    const s = String(v || "").trim().toUpperCase();
    if (s) return s;
  }
  return "";
}

/**
 * Custom order: QCOR_ → /quote, COR_/ORD_/CO_ → /api.
 * @returns {boolean|undefined}
 */
export function resolveCustomOrderIsQuote(source) {
  if (source == null) return undefined;
  if (typeof source === "boolean") return source;
  const asObj = typeof source === "object" ? source : null;
  if (asObj?.isQuote !== undefined && asObj?.isQuote !== null) {
    return Boolean(asObj.isQuote);
  }
  const doc = pickDocUpper(
    asObj?.voucherNumber,
    asObj?.voucher_number,
    asObj?.doc_number,
    asObj?.docNumber,
    asObj?.order_number,
    asObj?.co_number,
    asObj?.type,
    typeof source === "string" || typeof source === "number" ? source : "",
  );
  if (doc.includes("QCOR")) return true;
  // Non-quote custom orders (and unknown) use /api in mixed scope
  return false;
}

/**
 * Repairing order: QREP_ → /quote, REP_ → /api.
 * @returns {boolean|undefined}
 */
export function resolveRepairingOrderIsQuote(source) {
  if (source == null) return undefined;
  if (typeof source === "boolean") return source;
  const asObj = typeof source === "object" ? source : null;
  if (asObj?.isQuote !== undefined && asObj?.isQuote !== null) {
    return Boolean(asObj.isQuote);
  }
  const doc = pickDocUpper(
    asObj?.voucherNumber,
    asObj?.voucher_number,
    asObj?.doc_number,
    asObj?.docNumber,
    asObj?.order_number,
    asObj?.token_no,
    asObj?.type,
    typeof source === "string" || typeof source === "number" ? source : "",
  );
  if (doc.includes("QREP")) return true;
  // Non-quote repair orders (and unknown) use /api in mixed scope
  return false;
}
